/**
 * Application configuration utilities
 */

export type PaymentGateway = 'stripe' | 'abacatepay'
export type SupportedCurrency = 'USD' | 'BRL' | 'EUR'
export type SupportedCountry = 'US' | 'BR' | 'EU' | 'OTHER'

/**
 * Check if the application is in "Coming Soon" mode
 * When enabled, signups are disabled and users can only join the waitlist
 */
export function isComingSoonMode(): boolean {
  return process.env.NEXT_PUBLIC_COMING_SOON_MODE === 'true'
}

/**
 * Check if multi-currency pricing is enabled
 * When disabled, only USD pricing is shown regardless of user location
 */
export function isMultiCurrencyEnabled(): boolean {
  return process.env.NEXT_PUBLIC_ENABLE_MULTI_CURRENCY === 'true'
}

/**
 * Get the default currency (used when multi-currency is disabled)
 */
export function getDefaultCurrency(): SupportedCurrency {
  return (process.env.NEXT_PUBLIC_DEFAULT_CURRENCY as SupportedCurrency) || 'USD'
}

/**
 * Check if credit card is required for free trial signup
 * When enabled, users must provide a credit card to start the free trial
 * When disabled, users can start a free trial without payment info
 */
export function isTrialCreditCardRequired(): boolean {
  return process.env.NEXT_PUBLIC_REQUIRE_TRIAL_CREDIT_CARD === 'true'
}

/**
 * Check if payment method is required for free tier users
 * When enabled, even free tier users must provide payment info during onboarding
 * This reduces friction for upgrades later
 */
export function isPaymentRequiredForFreeTier(): boolean {
  return process.env.NEXT_PUBLIC_REQUIRE_PAYMENT_FOR_FREE_TIER === 'true'
}

/**
 * Get the free trial duration in days
 * Defaults to 14 days if not specified
 */
export function getTrialDurationDays(): number {
  const days = process.env.NEXT_PUBLIC_TRIAL_DURATION_DAYS
  return days ? parseInt(days, 10) : 14
}

/**
 * Check if Stripe is configured
 */
export function isStripeConfigured(): boolean {
  return !!process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
}

/**
 * Check if AbacatePay is configured (for Brazil)
 */
export function isAbacatePayConfigured(): boolean {
  return !!process.env.NEXT_PUBLIC_ABACATEPAY_PUBLIC_KEY
}

/**
 * Get the payment gateway for a specific country
 */
export function getPaymentGatewayForCountry(country: SupportedCountry): PaymentGateway {
  if (country === 'BR' && isAbacatePayConfigured()) {
    return 'abacatepay'
  }
  return 'stripe'
}

/**
 * Get the currency for a specific country
 */
export function getCurrencyForCountry(country: SupportedCountry): SupportedCurrency {
  switch (country) {
    case 'BR':
      return 'BRL'
    case 'EU':
      return 'EUR'
    default:
      return 'USD'
  }
}

/**
 * Get currency symbol for display
 */
export function getCurrencySymbol(currency: SupportedCurrency): string {
  switch (currency) {
    case 'BRL':
      return 'R$'
    case 'EUR':
      return '€'
    default:
      return '$'
  }
}

/**
 * Format price for display
 */
export function formatPrice(amount: number, currency: SupportedCurrency): string {
  const formatter = new Intl.NumberFormat(
    currency === 'BRL' ? 'pt-BR' : currency === 'EUR' ? 'de-DE' : 'en-US',
    {
      style: 'currency',
      currency,
    }
  )
  return formatter.format(amount)
}
